import React, {useCallback, useEffect, useState} from 'react';
import createAxiosInstance from "../support/AxiosSupport";
import PremiumUpsell from "./PremiumUpsell";
import {usePremiumGate} from "../hooks/usePremiumGate";

interface MonthWears {
    month: string;
    wears: number;
}

interface MostWornItem {
    id: string;
    name: string;
    categoryName: string | null;
    primaryPhotoUrl: string | null;
    wears: number;
}

interface WearAnalytics {
    months: number;
    totalWearsInPeriod: number;
    wearsByMonth: MonthWears[];
    mostWorn: MostWornItem[];
    consentRequired?: boolean;
}

interface Props {
    ownerId: string | null;
}

/**
 * Самостоятельный блок аналитики носибельности: столбчатая диаграмма носков по месяцам
 * и список самых носимых вещей. Грузит данные сам, чтобы не связываться с состоянием страницы.
 */
const WearAnalyticsSection: React.FC<Props> = ({ownerId}) => {
    const [data, setData] = useState<WearAnalytics | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(false);
    const {premiumGate, capturePremiumGate, resetPremiumGate} = usePremiumGate();

    const load = useCallback(async () => {
        setLoading(true);
        setError(false);
        resetPremiumGate();
        try {
            const api = await createAxiosInstance();
            const params = ownerId ? {ownerId} : {};
            const res = await api.get<WearAnalytics>('/wear-analytics', {params});
            setData(res.data);
        } catch (e) {
            // Premium-гейт (402/403 с подсказкой апгрейда) — это не сбой загрузки, а апселл:
            // показываем понятное объяснение с кнопкой апгрейда, а не error-state с retry,
            // который всегда снова упрётся в тот же гейт (CTHS-74). Реальные сбои — как раньше.
            if (!capturePremiumGate(e, 'Premium-аналитика')) {
                setError(true);
            }
        } finally {
            setLoading(false);
        }
    }, [ownerId, capturePremiumGate, resetPremiumGate]);

    useEffect(() => {
        load();
    }, [load]);

    if (loading) {
        return (
            <section data-testid="section-wear-analytics-loading" className="animate-pulse">
                <div className="h-5 w-48 bg-gray-200 dark:bg-ink-700 rounded mb-3"/>
                <div className="h-32 bg-gray-100 dark:bg-ink-800 rounded-xl"/>
            </section>
        );
    }

    if (premiumGate) {
        return (
            <PremiumUpsell
                testId="section-wear-analytics-premium"
                title={premiumGate.title}
                description={premiumGate.description}
            />
        );
    }

    if (error) {
        return (
            <section data-testid="section-wear-analytics-error" className="rounded-xl border border-gray-200 dark:border-ink-700 p-4">
                <p className="text-sm text-gray-500 dark:text-gray-400 mb-2">
                    Не удалось загрузить аналитику носки
                </p>
                <button
                    type="button"
                    onClick={load}
                    data-testid="section-wear-analytics-retry"
                    className="px-3 py-1.5 rounded-xl bg-brand-600 text-white text-sm hover:bg-brand-700"
                >
                    Повторить
                </button>
            </section>
        );
    }

    const analytics = data!;
    // Профилирование отключено — страница «Аналитика» показывает единое уведомление выше;
    // самостоятельный блок не дублирует его.
    if (analytics.consentRequired) {
        return null;
    }
    if (analytics.totalWearsInPeriod === 0) {
        return (
            <section data-testid="section-wear-analytics-empty">
                <h2 className="text-lg font-semibold mb-3 text-gray-900 dark:text-gray-100">
                    Носибельность
                </h2>
                <p className="text-gray-500 dark:text-gray-400 text-sm">
                    За последние {analytics.months} мес. отметок о носке нет — отмечайте носки, и здесь появится динамика.
                </p>
            </section>
        );
    }

    const maxWears = Math.max(1, ...analytics.wearsByMonth.map(m => m.wears));

    return (
        <section data-testid="section-wear-analytics" className="space-y-4">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                Носибельность · {analytics.totalWearsInPeriod} носков за {analytics.months} мес.
            </h2>

            <div
                className="flex items-end gap-2 h-32 rounded-xl border border-gray-200 dark:border-ink-700 p-3"
                data-testid="wear-analytics-chart"
                role="img"
                aria-label={`Носки по месяцам за последние ${analytics.months} месяцев`}
            >
                {analytics.wearsByMonth.map(m => (
                    <div key={m.month} className="flex-1 flex flex-col items-center justify-end h-full">
                        <span className="text-xs text-gray-500 dark:text-gray-400 mb-1">{m.wears}</span>
                        <div
                            className="w-full rounded-t bg-brand-500 dark:bg-brand-400 min-h-[2px]"
                            style={{height: `${(m.wears / maxWears) * 100}%`}}
                            title={`${m.month}: ${m.wears}`}
                        />
                        <span className="text-[10px] text-gray-400 mt-1">{m.month.slice(5)}</span>
                    </div>
                ))}
            </div>

            {analytics.mostWorn.length > 0 && (
                <div>
                    <h3 className="text-sm font-medium mb-2 text-gray-700 dark:text-gray-300">
                        Самые носимые
                    </h3>
                    <ul className="space-y-2">
                        {analytics.mostWorn.map(item => (
                            <li key={item.id}
                                className="flex justify-between items-center rounded-xl border border-gray-200 dark:border-ink-700 p-3">
                                <span className="text-gray-900 dark:text-gray-100">
                                    {item.name}
                                    {item.categoryName &&
                                        <span className="text-gray-500"> · {item.categoryName}</span>}
                                </span>
                                <span className="text-sm font-semibold text-gray-700 dark:text-gray-200">
                                    {item.wears} носков
                                </span>
                            </li>
                        ))}
                    </ul>
                </div>
            )}
        </section>
    );
};

export default WearAnalyticsSection;
